<script setup>
import { router } from '@inertiajs/vue3';
import { computed, ref, getCurrentInstance } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import CompanyFormModal from '@/components/admin/CompanyFormModal.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const confirm = useConfirm();
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    title: String,
    companies: Object,
});

const companiesData = computed(() => {
    return props.companies.data || [];
});

const loading = ref(false);

// Modal state
const showModal = ref(false);
const selectedCompany = ref(null);

const openCreateModal = () => {
    selectedCompany.value = null;
    showModal.value = true;
};

const openEditModal = (company) => {
    selectedCompany.value = { ...company };
    showModal.value = true;
};

const onModalSaved = () => {
    router.reload({ only: ['companies'] });
};

const destroy = (company) => {
    confirm.require({
        message: `${$t('Are you sure you want to delete')} ${company.name}?`,
        header: $t('Delete Confirmation'),
        icon: 'pi pi-exclamation-triangle',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: () => {
            router.delete(route('companies.destroy', company.id), {
                preserveScroll: true,
                preserveState: true,
                onStart: () => showLoading('Deleting company...'),
                onFinish: () => hideLoading(),
                onSuccess: () => {
                    toast.add({ severity: 'success', summary: 'Success', detail: 'Company deleted successfully', life: 3000 });
                },
                onError: () => {
                    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete company', life: 3000 });
                }
            });
        }
    });
};

const onPage = (event) => {
    const page = event.page + 1;
    router.get(route('companies.index'), { page }, {
        preserveState: true,
        preserveScroll: true,
        onStart: () => showLoading('Loading companies...'),
        onFinish: () => hideLoading()
    });
};

const paginatorTemplate = {
    layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport',
    CurrentPageReport: 'Showing {first} to {last} of {totalRecords} entries'
};

const getStatusSeverity = (isActive) => {
    return isActive ? 'success' : 'danger';
};
</script>

<template>
    <AdminMainLayout>
        <ConfirmDialog />

        <!-- Company Form Modal -->
        <CompanyFormModal
            v-model="showModal"
            :company="selectedCompany"
            @saved="onModalSaved"
        />

        <div class="flex justify-between items-center mb-4">
            <h2 class="text-2xl font-semibold text-gray-900">{{ $t('Companies') }}</h2>
            <Button :label="$t('Add Company')" icon="pi pi-plus" class="p-button-success" @click="openCreateModal" />
        </div>

        <DataTable
            :value="companiesData"
            :paginator="true"
            :rows="15"
            :totalRecords="companies.total"
            :lazy="true"
            @page="onPage"
            :loading="loading"
            dataKey="id"
            responsiveLayout="scroll"
            :paginatorTemplate="paginatorTemplate.layout"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            class="p-datatable-sm">

            <template #empty>
                <div class="text-center py-4">{{ $t('No companies found.') }}</div>
            </template>

            <Column field="name" :header="$t('Name')" :sortable="true" style="min-width: 200px">
                <template #body="slotProps">
                    <span class="text-blue-600 hover:text-blue-800 font-medium cursor-pointer" @click="openEditModal(slotProps.data)">
                        {{ slotProps.data.name }}
                    </span>
                </template>
            </Column>

            <Column field="legal_name" :header="$t('Legal Name')" :sortable="true" style="min-width: 200px">
                <template #body="slotProps">
                    {{ slotProps.data.legal_name || '-' }}
                </template>
            </Column>

            <Column field="document_number" :header="$t('Document')" style="min-width: 150px">
                <template #body="slotProps">
                    <div v-if="slotProps.data.document_number">
                        <small class="text-gray-500">{{ slotProps.data.document_type }}</small><br>
                        {{ slotProps.data.document_number }}
                    </div>
                    <span v-else>-</span>
                </template>
            </Column>

            <Column field="bank_accounts_count" :header="$t('Bank Accounts')" style="min-width: 120px">
                <template #body="slotProps">
                    <Tag :value="slotProps.data.bank_accounts_count || 0" severity="info" />
                </template>
            </Column>

            <Column field="is_active" :header="$t('Status')" style="min-width: 100px">
                <template #body="slotProps">
                    <Tag
                        :value="slotProps.data.is_active ? $t('Active') : $t('Inactive')"
                        :severity="getStatusSeverity(slotProps.data.is_active)"
                    />
                </template>
            </Column>

            <Column :header="$t('Actions')" :exportable="false" style="min-width: 150px">
                <template #body="slotProps">
                    <div class="flex gap-2">
                        <Button icon="pi pi-pencil" class="p-button-rounded p-button-text p-button-info"
                            @click="openEditModal(slotProps.data)" v-tooltip.top="$t('Edit')" />
                        <Button icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger"
                            @click="destroy(slotProps.data)" v-tooltip.top="$t('Delete')" />
                    </div>
                </template>
            </Column>
        </DataTable>
    </AdminMainLayout>
</template>

<style scoped>
.card {
    background: white;
    padding: 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}
</style>
